import { useQuery } from '@apollo/client';
import { Box, Button, Switch, Typography, type DialogProps } from '@mui/material';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { ContentGrid, DialogRoot } from '~/components/inputs/MediaSelect/components';
import { FolderContextProvider, useAppContext } from '~/contexts';
import type { Zone } from '~/generated/graphql';
import { ContentAddDialogDocument } from './ContentAddDialog.generated';

/* GraphQL */ `#graphql
query ContentAddDialog($networkId: Int!) {
  network(id: $networkId) {
    contentFolderTree
    contentFolders {
      canUpdate {
        ...AuthorizationResult
      }
      id
      name
    }
    id
  }
}
`;

export interface ContentAddDialogProps extends Omit<DialogProps, 'onChange'> {
  onClose: () => void;
  onChange: (selectedIds: string[]) => void;
  contentIds?: string[];
  zoneFilter: Pick<Zone, 'height' | 'name' | 'width'>;
}

export const ContentAddDialog = ({
  onClose,
  onChange,
  contentIds,
  zoneFilter,
  ...props
}: ContentAddDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [selectedContentIds, setSelectedContentIds] = useState<string[]>(contentIds ?? []);

  const { data } = useQuery(ContentAddDialogDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const handleItemClick = (id: string) => {
    setSelectedContentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const clearState = () => {
    setSelectedContentIds([]);
  };

  const [useAspectRatio, toggleAspectRatio] = useState(false);
  const aspectRatio = useMemo(() => zoneFilter.width / zoneFilter.height, [zoneFilter]);

  if (data?.network == null) return null;

  return (
    <FolderContextProvider
      folderTree={data.network.contentFolderTree}
      folders={data.network.contentFolders}
    >
      <Dialog
        PaperComponent={DialogRoot}
        TransitionProps={{ onExited: clearState }}
        fullWidth
        maxWidth="lg"
        onClose={onClose}
        {...props}
      >
        <DialogTitle onClose={onClose}>
          {t('pages.shows.detail.dialogs.content_add.title')}
        </DialogTitle>

        <DialogContent sx={{ overflow: 'hidden' }}>
          <DialogContentTitle>
            <Trans
              i18nKey="pages.shows.detail.dialogs.content_add.description"
              values={{ zoneName: zoneFilter.name }}
              components={{ 0: <strong /> }}
            />
          </DialogContentTitle>
          <ContentGrid
            actions={
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Switch
                  checked={useAspectRatio}
                  onChange={() => toggleAspectRatio((x) => !x)}
                  size="small"
                />
                <Typography noWrap>
                  <Trans
                    i18nKey="pages.shows.detail.dialogs.content_add.aspect_ratio_filter"
                    values={{
                      zoneName: zoneFilter.name,
                      zoneWidth: zoneFilter.width,
                      zoneHeight: zoneFilter.height,
                    }}
                    components={{ 0: <strong /> }}
                  />
                </Typography>
              </Box>
            }
            aspectRatio={useAspectRatio ? aspectRatio : undefined}
            onClick={handleItemClick}
            rememberLastFolder
            selectedIds={selectedContentIds}
          />
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {t('common.dialogs.cancel')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={selectedContentIds.length === 0}
            onClick={() => {
              onClose();
              onChange(selectedContentIds);
            }}
          >
            {t('pages.shows.detail.dialogs.content_add.add_button', {
              count: selectedContentIds.length,
              plural: t('pages.shows.detail.dialogs.content_add.item_plural', {
                count: selectedContentIds.length,
              }),
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </FolderContextProvider>
  );
};
